import React, { useEffect, useMemo, useState } from 'react';
import { MessageSquare, Save } from 'lucide-react';
import { scheduleTextReminderApi } from '../services/api';
import {
  Schedule,
  ScheduleTextReminderDay,
  ScheduleTextReminderMode,
} from '../types';
import { applyTextReminderMode, defaultTextReminderDays } from '../utils/scheduleTextReminders';

interface ScheduleTextRemindersPanelProps {
  schedules: Schedule[];
}

export default function ScheduleTextRemindersPanel({ schedules }: ScheduleTextRemindersPanelProps) {
  const allSchedules = useMemo(
    () => [...schedules].sort((a, b) => a.name.localeCompare(b.name)),
    [schedules]
  );
  const [scheduleId, setScheduleId] = useState<number>(allSchedules[0]?.id || 0);
  const [days, setDays] = useState<ScheduleTextReminderDay[]>(defaultTextReminderDays());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!scheduleId && allSchedules[0]?.id) {
      setScheduleId(allSchedules[0].id);
    }
  }, [allSchedules, scheduleId]);

  useEffect(() => {
    if (!scheduleId) {
      setDays(defaultTextReminderDays());
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await scheduleTextReminderApi.get(scheduleId);
        if (!cancelled) {
          setDays(data.days?.length === 7 ? data.days : defaultTextReminderDays());
        }
      } catch (error) {
        console.error('Error loading text reminders:', error);
        if (!cancelled) {
          alert('Failed to load text reminders');
          setDays(defaultTextReminderDays());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [scheduleId]);

  const setDay = (dayOfWeek: number, patch: Partial<ScheduleTextReminderDay>) => {
    setDays((prev) =>
      prev.map((day) => (day.dayOfWeek === dayOfWeek ? { ...day, ...patch } : day))
    );
  };

  const handleModeChange = (day: ScheduleTextReminderDay, mode: ScheduleTextReminderMode) => {
    const next = applyTextReminderMode(day, mode);
    setDay(day.dayOfWeek, { mode: next.mode, extraDays: next.extraDays });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!scheduleId) {
      alert('Please select a schedule.');
      return;
    }
    const plusWithoutX = days.find(
      (day) => day.mode === 'SameDayPlus' && (day.extraDays < 1 || day.extraDays > 14)
    );
    if (plusWithoutX) {
      alert('Next X days must be between 1 and 14.');
      return;
    }
    setSaving(true);
    try {
      await scheduleTextReminderApi.save({
        scheduleId,
        days: days.map((day) => ({
          dayOfWeek: day.dayOfWeek,
          mode: day.mode,
          extraDays: day.mode === 'SameDayPlus' ? day.extraDays : 0,
        })),
      });
      alert('Text reminders saved.');
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to save text reminders';
      alert(typeof message === 'string' ? message : 'Failed to save text reminders');
    } finally {
      setSaving(false);
    }
  };

  if (allSchedules.length === 0) {
    return (
      <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
        <MessageSquare className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-neutral-900 mb-2">No schedules found</h3>
        <p className="text-neutral-600">Create a schedule first, then configure weekday text reminders.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold">Text Reminders</h2>
          <p className="text-sm md:text-base text-neutral-600">
            For each weekday, choose same-day shifts, same-day plus the next X days, or no reminders.
            Saving stores the setup only — texts are not sent yet.
          </p>
        </div>
      </div>

      <form onSubmit={handleSave}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-neutral-700 mb-1">Schedule</label>
          <select
            value={scheduleId}
            onChange={(e) => setScheduleId(Number(e.target.value))}
            className="w-full sm:w-80 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {allSchedules.map((schedule) => (
              <option key={schedule.id} value={schedule.id}>
                {schedule.name}
                {!schedule.isActive ? ' (Inactive)' : ''}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="py-12 text-center text-neutral-600">Loading text reminders...</div>
        ) : (
          <>
            <div className="hidden md:block bg-white border border-neutral-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-neutral-700 w-36">Day</th>
                    <th className="text-left p-4 text-sm font-medium text-neutral-700">Reminder</th>
                    <th className="text-left p-4 text-sm font-medium text-neutral-700 w-48">
                      Next X days
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((day) => (
                    <tr key={day.dayOfWeek} className="border-b border-neutral-100">
                      <td className="p-4 font-medium text-neutral-900">{day.dayName}</td>
                      <td className="p-4">
                        <select
                          value={day.mode}
                          onChange={(e) =>
                            handleModeChange(day, e.target.value as ScheduleTextReminderMode)
                          }
                          className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="None">No reminders</option>
                          <option value="SameDay">Same day shifts</option>
                          <option value="SameDayPlus">Same day and next X days</option>
                        </select>
                      </td>
                      <td className="p-4">
                        {day.mode === 'SameDayPlus' ? (
                          <input
                            type="number"
                            min={1}
                            max={14}
                            value={day.extraDays}
                            onChange={(e) =>
                              setDay(day.dayOfWeek, {
                                extraDays: Number(e.target.value) || 0,
                              })
                            }
                            className="w-24 px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <span className="text-sm text-neutral-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3">
              {days.map((day) => (
                <div key={day.dayOfWeek} className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="font-medium mb-3">{day.dayName}</div>
                  <select
                    value={day.mode}
                    onChange={(e) => handleModeChange(day, e.target.value as ScheduleTextReminderMode)}
                    className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="None">No reminders</option>
                    <option value="SameDay">Same day shifts</option>
                    <option value="SameDayPlus">Same day and next X days</option>
                  </select>
                  {day.mode === 'SameDayPlus' && (
                    <label className="block mt-3 text-sm text-neutral-700">
                      Next X days
                      <input
                        type="number"
                        min={1}
                        max={14}
                        value={day.extraDays}
                        onChange={(e) =>
                          setDay(day.dayOfWeek, { extraDays: Number(e.target.value) || 0 })
                        }
                        className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </label>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end mt-6">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save reminders'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
